import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";
import { apiClient, type AccountProfileSummary } from "@slumbr/core";

interface ProfileSwitcherStore {
  profiles: AccountProfileSummary[];
  activeProfileId: string | null;
  setProfiles: (next: AccountProfileSummary[]) => void;
  setActiveProfileId: (id: string | null) => void;
  /** Adopt the server-remembered persona on a device that has no local
   * choice yet (fresh device / cleared storage). No-op when a local choice
   * exists — the user's explicit local pick wins. */
  hydrateActiveProfileFromServer: (id: string | null | undefined) => void;
  upsertProfile: (profile: AccountProfileSummary) => void;
}

/** Persist the persona choice server-side (best-effort) so the NEXT device
 * this account signs into can restore it. Fresh devices previously had no
 * way to know the persona → no X-Slumbr-Profile header → wrong data
 * partition → empty chat list. */
function persistActiveProfileToServer(id: string | null) {
  void apiClient
    .post("/api/user/active-profile", { profileId: id })
    .catch((err) => console.warn("[profiles] active-profile sync failed", err));
}

/** Tracks the active persona for the current account. Single source of truth
 * for "which face am I wearing right now". The active profile id is mirrored
 * to localStorage so reloads keep the chosen persona, and to the server so
 * fresh devices restore it. */
export const useProfileSwitcherStore = create<ProfileSwitcherStore>()(
  persist(
    (set, get) => ({
      profiles: [],
      activeProfileId: null,
      setProfiles: (next) => set({ profiles: next }),
      setActiveProfileId: (id) => {
        set({ activeProfileId: id });
        persistActiveProfileToServer(id);
      },
      hydrateActiveProfileFromServer: (id) => {
        if (!id) return;
        // "Local explicit choice wins" — judged by what is STORED, not by memory: sign-out clears the stored key
        // (clearTokens) but leaves this tab's in-memory id, which used to block the next account from adopting
        // its own remembered persona and kept sending the previous user's profile id.
        if (readStoredActiveProfileId()) return;
        set({ activeProfileId: id });
      },
      upsertProfile: (profile) =>
        set((state) => ({
          profiles: state.profiles.some((entry) => entry.userId === profile.userId)
            ? state.profiles.map((entry) => (entry.userId === profile.userId ? profile : entry))
            : [...state.profiles, profile],
        })),
    }),
    {
      name: "slumbr-profile-switcher",
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({ activeProfileId: state.activeProfileId }),
    }
  )
);

/** The persona choice as persisted (localStorage), independent of this tab's in-memory copy. */
function readStoredActiveProfileId(): string | null {
  try {
    const raw = localStorage.getItem("slumbr-profile-switcher");
    const id = raw ? (JSON.parse(raw) as { state?: { activeProfileId?: string | null } }).state?.activeProfileId : null;
    return typeof id === "string" && id ? id : null;
  } catch {
    return null;
  }
}

/** Sign-in starts from a clean switcher: the stored choice (null after a sign-out — clearTokens removes it) and an
 *  empty profile list, so nothing the previous account left in this tab's memory can be persisted back or block the
 *  new account from adopting its own remembered persona. Call before any profile fetch, unconditionally. */
export function syncActiveProfileFromStorage(): void {
  useProfileSwitcherStore.setState({ activeProfileId: readStoredActiveProfileId(), profiles: [] });
}

// One persona per browser. A persona switch or sign-out in ANOTHER tab reloads this one: identity is woven through
// the user store, realtime leader/subscriptions, per-persona caches and in-flight requests, and a hot swap of just the
// switcher left those on the old persona (cross-persona events/data). A reload is a clean identity transition.
if (typeof window !== "undefined") {
  window.addEventListener("storage", (e) => {
    if (e.key !== "slumbr-profile-switcher" && e.key !== null) return;
    if (readStoredActiveProfileId() === useProfileSwitcherStore.getState().activeProfileId) return; // no real change
    window.location.reload();
  });
}
